import React, { useState, useCallback, useEffect, useRef } from "react";
import { Col, Row, Container } from "reactstrap";
import { EmailOutlined, Settings } from "@mui/icons-material";
import { AlertColor } from "@mui/material";
import { useWizard } from "react-use-wizard";
import { useFormContext } from "react-hook-form";
import * as yup from "yup";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import WizardFormStep from "../../components/WizardFormStep";
import FormInput from "../../../../../../toolkit/formInput";
import PrimaryButton from "../../../../../../toolkit/primaryButton";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import ICharityEmailSetup from "../../../../../../types/ICharityEmailSetup";
import { useLoading } from "../../../../../../hooks/useLoading";
import { DomainService } from "../../../../../../services/domainService";
import {
  getDnsDelegateStatus,
  sendDnsDelegateEmail,
  DnsDelegateStatusResponse,
} from "../../../../../../utils/dnsDelegateApi";
import { useDnsEntries } from "../hooks/useDnsEntries";
import DnsSetupPanel from "../components/DnsSetupPanel";
import DnsAuthorizationComplete from "./components/DnsAuthorizationComplete";
import OnboardingStepNotice from "../../components/OnboardingStepNotice";
import { formatOnboardingEmailDeliveryError } from "../../utils/formatOnboardingEmailDeliveryError";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";

export const dnsConfigurationStepSchema = yup.object({
  dnsDelegateEmail: yup.string().trim().email("Please enter a valid email address").notRequired(),
});

type DnsView = "options" | "selfSetup" | "emailForm";

interface DnsConfigurationStepProps extends ServiceCallerProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  emailSetupData?: ICharityEmailSetup | null;
  onExit?: () => void;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
}

const formatSentTimestamp = (timestamp?: number) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleString();
};

const DnsConfigurationStep: React.FC<DnsConfigurationStepProps> = ({
  charityId,
  config,
  serviceAccessToken,
  onExit,
  showSnackbar,
}) => {
  const { nextStep } = useWizard();
  const [view, setView] = useState<DnsView>("options");
  const [showDnsWaiting, setShowDnsWaiting] = useState(false);
  const [delegateStatus, setDelegateStatus] = useState<DnsDelegateStatusResponse | null>(null);
  const { loading: isSendingEmail, runWithLoading } = useLoading();
  const { trigger, getValues, setValue, setError, clearErrors } = useFormContext();

  const { domainData, isFetchingDns, isDomainValid, error: dnsFetchError } = useDnsEntries({
    charityId,
    config,
    serviceAccessToken,
  });

  useEffect(() => {
    if (!isFetchingDns && isDomainValid) {
      nextStep();
    }
  }, [isFetchingDns, isDomainValid, nextStep]);

  const delegateStatusCharityRef = useRef<string | null>(null);

  useEffect(() => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      return;
    }
    if (delegateStatusCharityRef.current === charityId) {
      return;
    }

    delegateStatusCharityRef.current = charityId;

    getDnsDelegateStatus(config.adminServiceUrl, charityId, serviceAccessToken)
      .then(setDelegateStatus)
      .catch(() => {
        // banner is optional
      });
  }, [charityId, config?.adminServiceUrl, serviceAccessToken]);

  const handleEntriAuthorization = useCallback(async () => {
    try {
      const validateDomainResponse = await DomainService.validateDomainDns(
        config,
        charityId,
        serviceAccessToken
      );

      if (validateDomainResponse.valid) {
        nextStep();
      } else {
        setShowDnsWaiting(true);
      }
    } catch {
      setShowDnsWaiting(true);
    }
  }, [config, charityId, serviceAccessToken, nextStep]);

  const handleOpenSelfSetup = () => {
    setView("selfSetup");
  };

  const handleOpenEmailForm = () => {
    setValue("dnsDelegateEmail", "");
    clearErrors("dnsDelegateEmail");
    setView("emailForm");
  };

  const handleBackToOptions = () => {
    setView("options");
    setValue("dnsDelegateEmail", "");
    clearErrors("dnsDelegateEmail");
  };

  const handleSendDelegateEmail = async () => {
    clearErrors("dnsDelegateEmail");

    const isValid = await trigger("dnsDelegateEmail");
    if (!isValid) {
      return;
    }

    const recipientEmail = getValues("dnsDelegateEmail")?.trim();

    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      const message = "Unable to connect to the admin service. Please refresh and try again.";
      setError("dnsDelegateEmail", { message });
      showSnackbar?.(message, "error");
      return;
    }

    await runWithLoading(async () => {
      try {
        const response = await sendDnsDelegateEmail(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken,
          recipientEmail
        );
        setDelegateStatus({
          lastSentEmail: response.lastSentEmail,
          lastSentAt: response.lastSentAt,
          hasActiveToken: true,
          deliveryFailed: false,
        });
        showSnackbar?.(
          `DNS setup link successfully sent to ${response.lastSentEmail}.`,
          "success"
        );
        setValue("dnsDelegateEmail", "");
        setView("options");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to send email.";
        setError("dnsDelegateEmail", { message });
        showSnackbar?.(message, "error");
      }
    });
  };

  const renderOptionCard = (
    leading: React.ReactNode,
    title: string,
    subtitle: string,
    buttonLabel: string,
    onButtonClick: () => void,
    buttonDisabled?: boolean,
    buttonLoading?: boolean
  ) => (
    <div className={`${s.optionCardContainer} ${s.optionCard}`}>
      <div className={s.leadingArea}>{leading}</div>
      <div className={s.textCol}>
        <div className={`d-flex flex-column ${s.textContainer}`}>
          <span className={s.elementTitle}>{title}</span>
          <span className={s.elementSubTitle}>{subtitle}</span>
        </div>
      </div>
      <div className={s.actionCol}>
        <PrimaryButton
          onClick={onButtonClick}
          variant="primary"
          className={s.actionButton}
          disabled={buttonDisabled}
          loading={buttonLoading}
          loadingText="Sending..."
        >
          {buttonLabel}
        </PrimaryButton>
      </div>
    </div>
  );

  const showDelegateBanner =
    !isDomainValid &&
    delegateStatus?.lastSentEmail &&
    delegateStatus?.lastSentAt &&
    !delegateStatus?.deliveryFailed;

  const showDelegateDeliveryError =
    !isDomainValid &&
    delegateStatus?.deliveryFailed &&
    delegateStatus?.lastSentEmail;

  if (showDnsWaiting) {
    return (
      <DnsAuthorizationComplete
        domain={domainData?.domain || ""}
        onContinue={() => onExit && onExit()}
      />
    );
  }

  if (view === "selfSetup") {
    return (
      <DnsSetupPanel
        mode="admin"
        domainData={domainData}
        isFetchingDns={isFetchingDns}
        dnsFetchError={dnsFetchError}
        config={config}
        charityId={charityId}
        serviceAccessToken={serviceAccessToken}
        onEntriAuthorizationSuccess={handleEntriAuthorization}
        onManualValidateSuccess={nextStep}
        autoLaunchEntri
      />
    );
  }

  if (view === "emailForm") {
    return (
      <WizardFormStep title="Email DNS setup link" subtitle="Send setup steps to your DNS or IT contact">
        <Container fluid className={s.baseContainer}>
          <Row className="mt-4 justify-content-center">
            <Col md="8">
              <FormInput
                name="dnsDelegateEmail"
                type="email"
                icon="fa-solid fa-envelope"
                placeholder="dns-admin@organization.org"
              />
            </Col>
          </Row>

          <Row className="justify-content-center mt-4 mb-4">
            <Col md={8} className="d-flex justify-content-center">
              <PrimaryButton
                onClick={handleSendDelegateEmail}
                loading={isSendingEmail}
                loadingText="Sending..."
                loadingAnimation="pulse"
                variant="primary"
              >
                Send email
              </PrimaryButton>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <span className={s.alternativeText}>
                Prefer to set up DNS yourself?{" "}
                <button type="button" className={s.alternativeLink} onClick={handleBackToOptions}>
                  Back to setup options
                </button>
              </span>
            </Col>
          </Row>
        </Container>
      </WizardFormStep>
    );
  }

  return (
    <WizardFormStep
      title="Configure DNS Settings"
      subtitle="Choose how you want to configure DNS for your sending domain"
      className={s.dnsStepContainer}
    >
      <Container fluid className={s.baseContainer}>
        {showDelegateDeliveryError && (
          <Row className="justify-content-center mb-4">
            <Col md="9">
              <OnboardingStepNotice variant="error" requiresAttention>
                {formatOnboardingEmailDeliveryError({
                  purpose: "DNS setup",
                  email: delegateStatus.lastSentEmail!,
                  timestamp: delegateStatus.lastSentAt,
                  reason: delegateStatus.deliveryError || delegateStatus.deliveryStatus,
                })}
              </OnboardingStepNotice>
            </Col>
          </Row>
        )}

        {showDelegateBanner && (
          <Row className="justify-content-center mb-4">
            <Col md="9">
              <OnboardingStepNotice variant="success">
                DNS setup link sent to <strong>{delegateStatus.lastSentEmail}</strong> on{" "}
                {formatSentTimestamp(delegateStatus.lastSentAt)}.
                You can still set up DNS yourself or send a new link.
              </OnboardingStepNotice>
            </Col>
          </Row>
        )}

        <Row className="mt-1 justify-content-center">
          <Col md="9" className={s.optionsStack}>
            {renderOptionCard(
              <Settings className={s.settingsIcon} />,
              "Set up myself",
              "Use automatic DNS setup or add records manually",
              "Set up DNS",
              handleOpenSelfSetup,
              isFetchingDns
            )}
            {renderOptionCard(
              <EmailOutlined className={s.emailIcon} />,
              "Send email",
              "Email DNS setup steps to your IT contact",
              "Send instructions",
              handleOpenEmailForm
            )}
          </Col>
        </Row>
      </Container>
    </WizardFormStep>
  );
};

export default serviceCaller(withStyles(s)(DnsConfigurationStep));
